/*
 * The 28-day cycle on screen: which day the home is on, when the next order is due, and the
 * steps the pharmacy ticks off for the cycle coming up.
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiError, api } from './api.ts';
import type { User } from './api.ts';

export type Cycle = {
  number: number;
  startsOn: string;
  endsOn: string;
  day: number | null;
  orderBy: string;
  nextStartsOn: string;
  days: number;
  isCurrent: boolean;
  state: {
    orderedAt: string | null; orderedBy: string;
    preparedAt: string | null; preparedBy: string;
    checkedAt: string | null; checkedBy: string;
    deliveredAt: string | null; deliveredBy: string;
  } | null;
};

/*
 * Making up and the pharmacist's check happen on the bench and are not ticked off here; the
 * pharmacy's part in the system is approving the home's medicines once the trays are packed.
 */
const STEPS: Array<{ id: 'ordered' | 'delivered'; label: string; who: string }> = [
  { id: 'ordered', label: 'Ordered', who: 'the home' },
  { id: 'delivered', label: 'Delivered', who: 'the pharmacy' },
];

/* The one-line reminder that sits above the round. */
export function CycleBanner({ homeId }: { homeId: string }) {
  const [current, setCurrent] = useState<Cycle | null>(null);

  useEffect(() => {
    api
      .get<{ current: Cycle }>(`/api/homes/${homeId}/cycles`)
      .then((data) => setCurrent(data.current))
      .catch(() => setCurrent(null));
  }, [homeId]);

  if (!current?.day) return null;

  const daysLeft = current.days - current.day;
  const orderSoon = daysLeft <= 10;

  return (
    <p className={`notice ${orderSoon ? 'warn' : ''}`}>
      <strong>Day {current.day} of {current.days}</strong> · this cycle ends {formatDate(current.endsOn)}
      {orderSoon ? ` · the next order is due by ${formatDate(nextOrderBy(current))}` : ''}
    </p>
  );
}

export function CycleScreen({ homeId, user }: { homeId: string; user: User }) {
  const [cycles, setCycles] = useState<Cycle[] | null>(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const data = await api.get<{ cycles: Cycle[] }>(`/api/homes/${homeId}/cycles`);
      setCycles(data.cycles);
      setError('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the cycles.');
    }
  }, [homeId]);

  useEffect(() => {
    void load();
  }, [load]);

  const mark = useCallback(
    async (cycle: Cycle, step: string) => {
      try {
        await api.post(`/api/homes/${homeId}/cycles/${cycle.startsOn}/${step}`);
        await load();
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'That did not save.');
      }
    },
    [homeId, load],
  );

  const isPharmacy = user.roles.includes('pharmacy');

  return (
    <section>
      <div className="head">
        <h1>28-day cycles</h1>
        <button type="button" className="quiet" onClick={() => void load()}>Refresh</button>
      </div>
      <p className="muted">
        Medicines are supplied in 28-day cycles. The order goes in a week before the next one starts,
        and the MAR chart runs over the same dates.
      </p>

      {error ? <p className="error" role="alert">{error}</p> : null}
      {cycles === null ? <p className="muted">Loading…</p> : null}

      <ul className="list">
        {(cycles ?? []).map((cycle) => {
          const past = cycle.endsOn < todayKey();
          return (
            <li key={cycle.startsOn}>
              <div className={`row static column dose ${cycle.isCurrent ? 'due' : ''}`}>
                <div className="row-line">
                  <span className="row-main">
                    Cycle {cycle.number}
                    {cycle.isCurrent ? ' · now' : past ? ' · finished' : ' · coming up'}
                  </span>
                  <span className="time">{formatDate(cycle.startsOn)} – {formatDate(cycle.endsOn)}</span>
                </div>

                {cycle.isCurrent && cycle.day ? (
                  <>
                    <p className="muted small">Day {cycle.day} of {cycle.days}</p>
                    <div className="bar" aria-hidden="true">
                      <span style={{ width: `${Math.round((cycle.day / cycle.days) * 100)}%` }} />
                    </div>
                  </>
                ) : (
                  <p className="muted small">Order with the pharmacy by {formatDate(cycle.orderBy)}</p>
                )}

                <div className="chips">
                  {STEPS.map((step) => {
                    const at = cycle.state?.[`${step.id}At` as const];
                    const by = cycle.state?.[`${step.id}By` as const];
                    return (
                      <span key={step.id} className={`chip ${at ? 'done' : ''}`}>
                        {step.label}
                        {at ? `: ${by}` : ''}
                      </span>
                    );
                  })}
                </div>

                {!past ? (
                  <div className="row-actions">
                    {STEPS.filter((step) => !cycle.state?.[`${step.id}At` as const])
                      .filter((step) => (step.id === 'ordered' ? true : isPharmacy))
                      .map((step) => (
                        <button key={step.id} type="button" className="quiet" onClick={() => void mark(cycle, step.id)}>
                          Mark {step.label.toLowerCase()}
                        </button>
                      ))}
                  </div>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function nextOrderBy(cycle: Cycle): string {
  const when = new Date(`${cycle.nextStartsOn}T12:00:00`);
  when.setDate(when.getDate() - 7);
  return iso(when);
}

function iso(when: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${when.getFullYear()}-${pad(when.getMonth() + 1)}-${pad(when.getDate())}`;
}

function todayKey(): string {
  return iso(new Date());
}

function formatDate(date: string): string {
  const when = new Date(`${date}T12:00:00`);
  return when.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}
